import React, { useEffect, useState } from 'react';
import { MapPin, Phone, Mail, Clock, Instagram, Send } from 'lucide-react';
import { siteData } from '../data/mock';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    objective: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fadeIn');
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animated').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission - will be replaced with backend API
    setTimeout(() => {
      toast.success('Message envoyé ! Je vous recontacterai très rapidement.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        objective: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-page)] pt-20">
      {/* Hero */}
      <section className="section-padding bg-[var(--brand-dark)] text-white">
        <div className="container-max text-center">
          <div className="animated">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Contactez-moi
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Prêt à créer ton déclic ? Je réponds à toutes tes questions dans les plus brefs délais
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="section-padding">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="animated">
              <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)] mb-8">
                Informations de contact
              </h2>

              <div className="space-y-6 mb-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[var(--brand-primary)] flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-[var(--text-primary)] mb-1">
                      Adresse
                    </h3>
                    <p className="text-[var(--text-secondary)]">
                      {siteData.business.address}<br />
                      {siteData.business.city}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[var(--brand-primary)] flex items-center justify-center flex-shrink-0">
                    <Phone className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-[var(--text-primary)] mb-1">
                      Téléphone
                    </h3>
                    <a
                      href={`tel:${siteData.business.phone.replace(/\s/g, '')}`}
                      className="text-[var(--text-secondary)] hover:text-[var(--brand-primary)] transition-colors"
                    >
                      {siteData.business.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[var(--brand-primary)] flex items-center justify-center flex-shrink-0">
                    <Mail className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-[var(--text-primary)] mb-1">
                      Email
                    </h3>
                    <a
                      href={`mailto:${siteData.business.email}`}
                      className="text-[var(--text-secondary)] hover:text-[var(--brand-primary)] transition-colors"
                    >
                      {siteData.business.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[var(--brand-primary)] flex items-center justify-center flex-shrink-0">
                    <Clock className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-[var(--text-primary)] mb-1">
                      Horaires
                    </h3>
                    <p className="text-[var(--text-secondary)]">
                      {siteData.business.hours}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[var(--brand-primary)] flex items-center justify-center flex-shrink-0">
                    <Instagram className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-[var(--text-primary)] mb-1">
                      Instagram
                    </h3>
                    <a
                      href={siteData.business.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--text-secondary)] hover:text-[var(--brand-primary)] transition-colors"
                    >
                      @declic.coaching
                    </a>
                  </div>
                </div>
              </div>

              <div className="network-card">
                <p className="text-[var(--text-secondary)] text-lg">
                  ⭐ <strong>{siteData.business.rating}/5</strong> ({siteData.business.reviewCount} avis)
                </p>
                {siteData.business.lgbtqFriendly && (
                  <p className="text-[var(--text-secondary)] mt-3">
                    🏳️‍🌈 <strong>LGBTQ+ friendly</strong>
                  </p>
                )}
              </div>
            </div>

            {/* Contact Form */}
            <div className="animated">
              <div className="network-card">
                <h2 className="text-3xl font-bold text-[var(--text-primary)] mb-6">
                  Envoyer un message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-[var(--text-primary)] font-medium mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border-medium)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] transition-all"
                      placeholder="Votre nom"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-[var(--text-primary)] font-medium mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border-medium)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] transition-all"
                      placeholder="votre@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-[var(--text-primary)] font-medium mb-2">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border-medium)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] transition-all"
                      placeholder="06 XX XX XX XX"
                    />
                  </div>

                  <div>
                    <label htmlFor="objective" className="block text-[var(--text-primary)] font-medium mb-2">
                      Objectif principal *
                    </label>
                    <select
                      id="objective"
                      name="objective"
                      value={formData.objective}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border-medium)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] transition-all"
                    >
                      <option value="">Sélectionner un objectif</option>
                      <option value="perte-poids">Perte de poids</option>
                      <option value="prise-masse">Prise de masse musculaire</option>
                      <option value="remise-forme">Remise en forme</option>
                      <option value="confiance">Confiance en soi</option>
                      <option value="nutrition">Suivi nutritionnel</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-[var(--text-primary)] font-medium mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="5"
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border-medium)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] transition-all resize-none"
                      placeholder="Parlez-moi de vos objectifs, vos contraintes, vos questions..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span>Envoi en cours...</span>
                    ) : (
                      <>
                        <Send size={20} />
                        <span>Envoyer le message</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="animated">
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)] mb-8 text-center">
              Nous trouver
            </h2>
            <div className="rounded-3xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2641.4949!2d7.6835!3d48.5549!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDjCsDMzJzE3LjciTiA3wrA0MScwMC43IkU!5e0!3m2!1sfr!2sfr!4v1234567890"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localisation Déclic Coaching"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
